import { Pipe, PipeTransform } from "@angular/core";

@Pipe({
	name: "shortcutTitle",
})
export class ShortcutTitlePipe implements PipeTransform {
	private readonly stopwords = new Set([
		"de",
		"du",
		"des",
		"d",
		"la",
		"le",
		"les",
		"l",
		"et",
		"à",
		"au",
		"aux",
		"en",
		"pour",
		"par",
		"sur",
		"dans",
		"avec",
		"sans",
	]);

	transform(value: string | null | undefined, opts?: { maxLen?: number; minWordLen?: number }): string {
		if (!value) return "";

		const maxLen = opts?.maxLen ?? 16; // keep full title if <= this length
		const minWordLen = opts?.minWordLen ?? 2; // ignore tiny words when building acronym

		const title = value.trim().replace(/\s+/g, " ");
		if (title.length <= maxLen) return title;

		// Tokenize: split on spaces & hyphens, handle apostrophes (d', l', qu', etc.)
		const tokens = title
			.split(/[\s-]+/)
			.map((w) => this.stripApostrophePrefix(w))
			.filter(Boolean);

		// Build acronym from significant words (skip stopwords and very short words)
		const acronym = tokens
			.filter((w) => !this.stopwords.has(this.normalize(w)) && this.normalize(w).length >= minWordLen)
			.map((w) => this.firstLetter(w))
			.join("")
			.toUpperCase();

		// If we got a reasonable acronym (>=2 letters), use it; otherwise fall back to a neat truncation
		if (acronym.length >= 2) return acronym;

		// Fallback: truncate with ellipsis, keeping whole words when possible
		return this.smartTruncate(title, maxLen);
	}

	private firstLetter(word: string): string {
		// Take first alphabetic character (handles words starting with punctuation)
		const m = this.normalize(word).match(/[A-Za-z]/);
		return m ? m[0] : "";
	}

	private stripApostrophePrefix(word: string): string {
		// Remove leading segment before an apostrophe (e.g., d', l', qu')
		return word.replace(/^[A-Za-zÀ-ÖØ-öø-ÿ]'/, "");
	}

	private normalize(s: string): string {
		// Remove diacritics for consistent comparisons (Prématurée -> Prematuree)
		return s
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLowerCase();
	}

	private smartTruncate(s: string, maxLen: number): string {
		if (s.length <= maxLen) return s;
		const cut = s.slice(0, maxLen).lastIndexOf(" ");
		const at = cut > 0 ? cut : maxLen;
		return s.slice(0, at).trimEnd() + "…";
	}
}
